import axios from "./_axios";
import { API } from "./apiEndPoints";

function serializeJobCategoryListParams(params) {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      if (value.length > 0) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.join(","))}`);
      }
    } else if (value !== undefined && value !== "") {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
}

function jobCategoryCollectionUrl(accountId) {
  return `${API.ACCOUNT_LIST_V2}${accountId}/job-category/`;
}

function jobCategoryDetailUrl(accountId, categoryId) {
  return `${jobCategoryCollectionUrl(accountId)}${categoryId}/`;
}

function jobCategoryActionUrl(accountId, categoryId, action) {
  return `${jobCategoryDetailUrl(accountId, categoryId)}${action}/`;
}

/**
 * GET /api/common/v2/account/<id>/job-category/
 * Paginated list. Supports `search` (name), comma-separated `status`,
 * `archived`, and `used` (true = has positions, false = unused).
 */
export const getListOfJobCategories = (accountId, filters = {}, pagination = {}) => {
  const params = { ordering: filters.ordering || "name" };
  const trimmedSearch = typeof filters.search === "string" ? filters.search.trim() : "";
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.archived !== undefined) {
    params.archived = filters.archived;
  }
  if (filters.used !== undefined) {
    params.used = filters.used;
  }
  if (pagination.page != null) {
    params.page = pagination.page;
  }
  if (pagination.pageSize != null) {
    params.page_size = pagination.pageSize;
  }
  return axios()({
    method: "get",
    url: jobCategoryCollectionUrl(accountId),
    params,
    paramsSerializer: serializeJobCategoryListParams,
  });
};

export const getJobCategory = (accountId, categoryId) => {
  return axios()({
    method: "get",
    url: jobCategoryDetailUrl(accountId, categoryId),
  });
};

export const createJobCategory = (accountId, data) => {
  return axios()({
    method: "post",
    url: jobCategoryCollectionUrl(accountId),
    data,
  });
};

/**
 * PATCH /api/common/v2/account/<id>/job-category/<id>/
 * Writable: name, the four 0–100 scores, status, archived.
 * Returns the same read shape as GET.
 */
export const updateJobCategory = (accountId, categoryId, data) => {
  return axios()({
    method: "patch",
    url: jobCategoryDetailUrl(accountId, categoryId),
    data,
  });
};

export const deleteJobCategory = (accountId, categoryId) => {
  return axios()({
    method: "delete",
    url: jobCategoryDetailUrl(accountId, categoryId),
  });
};

/**
 * Lifecycle actions (ER-11768). Every transition is a chatter-logged backend
 * @action(detail=True, methods=["post"]) and must NOT go through the bare PATCH
 * (whose serializer only accepts the four score fields — name/status/archived
 * are deliberately excluded, so a generic PATCH silently no-ops).
 */
function jobCategoryLifecycleAction(accountId, categoryId, action, data) {
  return axios()({
    method: "post",
    url: jobCategoryActionUrl(accountId, categoryId, action),
    ...(data ? { data } : {}),
  });
}

/** POST .../job-category/{id}/activate/ — draft → active. */
export const activateJobCategory = (accountId, categoryId) =>
  jobCategoryLifecycleAction(accountId, categoryId, "activate");

/** POST .../job-category/{id}/retire/ — active → retired. */
export const retireJobCategory = (accountId, categoryId) =>
  jobCategoryLifecycleAction(accountId, categoryId, "retire");

/** POST .../job-category/{id}/reactivate/ — retired → active. */
export const reactivateJobCategory = (accountId, categoryId) =>
  jobCategoryLifecycleAction(accountId, categoryId, "reactivate");

/** POST .../job-category/{id}/archive/ — retired → archived. */
export const archiveJobCategory = (accountId, categoryId) =>
  jobCategoryLifecycleAction(accountId, categoryId, "archive");

/** POST .../job-category/{id}/unarchive/ — archived → retired. `name` (ER-11892) renames atomically. */
export const unarchiveJobCategory = (accountId, categoryId, name) =>
  jobCategoryLifecycleAction(accountId, categoryId, "unarchive", name ? { name } : undefined);

// ------------------------------------------------------------------ //
// Position-scoped assignment actions (ER-10772 / MDP-566)             //
//                                                                     //
// A Position subscribes to at most one current Job Category. The      //
// actions live on the PositionJobCategoryViewSet, nested under the    //
// position resource:                                                  //
//   POST .../position/<position_id>/job-category/assign/              //
//   POST .../position/<position_id>/job-category/unassign/            //
// Both are POST @actions (not bare PATCH) so the backend closes the   //
// present period and writes a chatter entry.                          //
// ------------------------------------------------------------------ //

function positionJobCategoryActionUrl(accountId, positionId, action) {
  return `${API.ACCOUNT_LIST_V2}${accountId}/position/${positionId}/job-category/${action}/`;
}

/**
 * POST .../position/<position_id>/job-category/assign/ — subscribe the
 * position to a Job Category (`assignment_type: "SUBSCRIBED"`). The backend
 * closes any present period and opens a new one (unassign-before-reassign is
 * enforced in the UI, not here). 400 JOB_CATEGORY_NOT_ACTIVE when the
 * category is not Active.
 */
export const assignPositionJobCategory = (accountId, positionId, data) => {
  return axios()({
    method: "post",
    url: positionJobCategoryActionUrl(accountId, positionId, "assign"),
    data,
  });
};

/**
 * POST .../position/<position_id>/job-category/unassign/ — end the position's
 * current subscription, leaving it unassigned. 400 JOB_CATEGORY_NOT_ASSIGNED
 * when there is no present period.
 */
export const unassignPositionJobCategory = (accountId, positionId) => {
  return axios()({
    method: "post",
    url: positionJobCategoryActionUrl(accountId, positionId, "unassign"),
  });
};
